// Angular
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

// Third party
import { BehaviorSubject, Observable, catchError, map, of, switchMap, tap } from 'rxjs';

// Local library
import { APP_TITLE, DOMAIN } from '../tokens';
import { SessionService } from './session.service';

/**
 * Flat key/copy map, as shipped in /assets/language/{code}.json
 */
export type LanguagePack = Record<string, string>;

/**
 * Values for the {name} tokens a pack entry declares, e.g. {ticketcount}.
 * Nullable because call sites commonly feed a formatting pipe straight in.
 */
export type TranslateParams = Record<string, string | number | null | undefined>;

export const DEFAULT_LANGUAGE = 'en';

// Legal entity behind the brand; mirrors SITE_TITLE_2 in frontend/js/amwager.js
const SITE_TITLE_2 = 'AmWest Entertainment';

// Call sites and packs disagree on casing and trailing punctuation, so compare
// copy on a relaxed form of it.
const indexKey = (copy: string) =>
  copy.toLowerCase().replace(/\s+/g, ' ').replace(/[.:*]+$/, '').trim();

const rankKey = (key: string) => (key.includes('.') ? 0 : 1);

/**
 * Loads the language packs ported from frontend/language and resolves copy
 * keys against the active one. Port of getLanguageObject/getTextByLanguage
 * in frontend/js/user.js.
 */
@Injectable({ providedIn: 'root' })
export class TranslationService {
  private readonly http: HttpClient;
  private readonly packs = new Map<string, LanguagePack>();
  private readonly _language$ = new BehaviorSubject<string>(DEFAULT_LANGUAGE);

  private strings: LanguagePack = {};
  private englishIndex: Map<string, string> | null = null;

  readonly language$: Observable<string> = this._language$.asObservable();

  constructor(
    httpBackend: HttpBackend,
    private readonly sessionService: SessionService,
    @Inject(APP_TITLE) private readonly appTitle: string,
    @Inject(DOMAIN) private readonly domain: string
  ) {
    // Bypass httpInterceptorFn: it stamps API headers on every request, drops
    // non-whitelisted calls for guests and reports failures as server errors —
    // none of which suit a static asset.
    this.http = new HttpClient(httpBackend);
  }

  /**
   * Currently active language code
   * @returns {string} Language code (e.g. 'es')
   */
  get language(): string {
    return this._language$.getValue();
  }

  /**
   * Activate the persisted language — call once at bootstrap
   * @returns {Observable<boolean>} Emits true when the pack is active
   */
  init(): Observable<boolean> {
    // English is kept resident: the other packs are incomplete, so it doubles
    // as the fallback for any key they omit.
    return this.load(DEFAULT_LANGUAGE)
      .pipe(switchMap(() => this.use(this.sessionService.getLanguage())));
  }

  /**
   * Activate a language, fetching its pack the first time it is used
   * @param {string} code Language code (e.g. 'es')
   * @returns {Observable<boolean>} Emits false when the pack cannot be fetched
   */
  use(code: string): Observable<boolean> {
    const language = code || DEFAULT_LANGUAGE;

    return this.load(language).pipe(
      map((pack) => {
        if (!pack) return false;
        this.activate(language, pack);
        return true;
      })
    );
  }

  /**
   * Fetch a pack once and keep it cached
   * @param {string} code Language code (e.g. 'es')
   * @returns {Observable<LanguagePack | null>} Null when the fetch fails
   */
  private load(code: string): Observable<LanguagePack | null> {
    const cached = this.packs.get(code);
    if (cached) return of(cached);

    return this.http.get<LanguagePack>(`assets/language/${code}.json`)
      .pipe(
        map((pack) => pack ?? {}),
        tap((pack) => {
          this.packs.set(code, pack);
          if (code === DEFAULT_LANGUAGE) this.englishIndex = null;
        }),
        catchError(() => of(null))
      );
  }

  /**
   * Resolve copy written in English at the call site, for dialog and alert text
   * built in TypeScript where the translate pipe cannot reach it
   * @param {string} text English copy, e.g. 'Insufficient Funds.'
   * @returns {string} Translated copy, or the input when the pack has no match
   */
  translateCopy(text: string): string {
    if (!text || !text.trim()) return text;

    return this.expandPlaceholders(this.copyFor(text) ?? text);
  }

  /**
   * Look copy up by its English wording rather than by key
   * @param {string} text English copy, e.g. 'Too Weak'
   * @returns {string | undefined} Active-language copy, or undefined when unknown
   */
  private copyFor(text: string): string | undefined {
    if (!this.englishIndex) this.englishIndex = this.buildEnglishIndex();
    const key = this.englishIndex.get(indexKey(text));
    if (!key) return undefined;

    const active = this.strings[key];
    return active && active.trim() ? active : this.packs.get(DEFAULT_LANGUAGE)?.[key];
  }

  /**
   * Index the English pack by copy so call-site text can find its key
   * @returns {Map<string, string>} Normalised English copy to language key
   */
  private buildEnglishIndex(): Map<string, string> {
    const english = this.packs.get(DEFAULT_LANGUAGE) ?? {};
    const index = new Map<string, string>();

    for (const [key, value] of Object.entries(english)) {
      if (typeof value !== 'string' || !value.trim()) continue;

      // Call sites spell the brand out where the packs hold a placeholder, so
      // index both wordings; the Set collapses copy without a placeholder.
      for (const wording of new Set([value, this.expandPlaceholders(value)])) {
        const id = indexKey(wording);
        // Namespaced keys are the curated ones, so let them win a collision.
        const held = index.get(id);
        if (held && rankKey(held) <= rankKey(key)) continue;
        index.set(id, key);
      }
    }

    return index;
  }

  /**
   * Resolve a copy key against the active pack
   * @param {string} key Language key (e.g. 'account.preferences.language')
   * @param {string} fallback Copy to use when neither pack has the key
   * @param {TranslateParams} params Values for the {name} tokens in the copy
   * @returns {string} Translated copy, the English copy, the fallback, or the key
   * @see translateCopy for text that arrives as English rather than a key
   */
  translate(key: string, fallback?: string, params?: TranslateParams): string {
    if (!key) return '';

    // The packs key whitelabel copy by brand name, so resolve it before lookup
    const lookup = key.includes('[whitelabel]')
      ? key.replace('[whitelabel]', this.appTitle)
      : key;

    const english = this.packs.get(DEFAULT_LANGUAGE) ?? {};
    // Some pack entries are blank placeholders; treat those as untranslated so
    // the label falls through to English rather than rendering as empty.
    const usable = (value?: string) => (value && value.trim() ? value : undefined);
    const copy =
      usable(this.strings[lookup]) ??
      usable(english[lookup]) ??
      usable(fallback) ??
      // Some copy reaches the pipe as English text rather than a key, e.g.
      // password-strength labels built in a non-Angular library.
      usable(this.copyFor(lookup)) ??
      lookup;

    return this.expandPlaceholders(copy, params);
  }

  /**
   * Swap the active pack and notify subscribers
   * @param {string} language Language code
   * @param {LanguagePack} pack Copy map for that language
   * @returns {void}
   */
  private activate(language: string, pack: LanguagePack): void {
    this.strings = pack;
    this._language$.next(language);
  }

  /**
   * Substitute the whitelabel placeholders baked into the packs, then any
   * caller-supplied values
   * @param {string} copy Raw copy from the pack
   * @param {TranslateParams} params Values for the {name} tokens in the copy
   * @returns {string} Copy with brand/domain placeholders and tokens resolved
   */
  private expandPlaceholders(copy: string, params?: TranslateParams): string {
    const expanded = copy
      .replace(/\{SITE_TITLE_2\}/g, SITE_TITLE_2)
      .replace(/\{SITE_TITLE\}/g, this.appTitle)
      .replace(/\{DOMAIN_ENDPOINT\}/g, this.domain);

    if (!params) return expanded;

    // Leave unknown tokens alone: a missing value should not blank the copy out
    return expanded.replace(/\{(\w+)\}/g, (token, name) =>
      name in params ? String(params[name] ?? '') : token
    );
  }
}
